import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  UserAccount, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserFilter, 
  UserDetailsResponse,
  ChangePasswordRequest,
  UserStatsOverview,
  LoginActivitySummary
} from '../../types/user';
import { apiClient } from '../../utils/apiClient';

interface UserManagementState {
  users: UserAccount[];
  selectedUser: UserAccount | null;
  userDetails: UserDetailsResponse | null;
  userStats: UserStatsOverview | null;
  loginActivity: LoginActivitySummary | null;
  loading: boolean;
  error: string | null;
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };
  filters: UserFilter;
}

const initialState: UserManagementState = {
  users: [],
  selectedUser: null,
  userDetails: null,
  userStats: null,
  loginActivity: null,
  loading: false,
  error: null,
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 10
  },
  filters: {
    page: 1,
    limit: 10
  }
};

// Async thunks
export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (filters: UserFilter, { rejectWithValue }) => {
    try {
      return await apiClient.getUsers(filters);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createUser = createAsyncThunk(
  'userManagement/createUser',
  async (userData: CreateUserRequest, { rejectWithValue }) => {
    try {
      return await apiClient.createUser(userData);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/updateUser',
  async (userData: UpdateUserRequest, { rejectWithValue }) => {
    try {
      const { id, ...updateData } = userData;
      return await apiClient.updateUser(id, updateData);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'userManagement/deleteUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      await apiClient.deleteUser(userId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// New API endpoints according to specification
export const fetchUserDetails = createAsyncThunk(
  'userManagement/fetchUserDetails',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await apiClient.getUserDetails(userId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'userManagement/toggleUserStatus',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await apiClient.toggleUserStatus(userId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  'userManagement/changeUserPassword',
  async ({ userId, passwordData }: { userId: number; passwordData: ChangePasswordRequest }, { rejectWithValue }) => {
    try {
      return await apiClient.changeUserPassword(userId, passwordData);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'userManagement/fetchUserStats',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient.getUserStats();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLoginActivity = createAsyncThunk(
  'userManagement/fetchLoginActivity',
  async (params: { days?: number; role?: string } = {}, { rejectWithValue }) => {
    try {
      return await apiClient.getLoginActivity(params);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const restoreUser = createAsyncThunk(
  'userManagement/restoreUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await apiClient.restoreUser(userId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<UserAccount | null>) => {
      state.selectedUser = action.payload;
    },
    setFilters: (state, action: PayloadAction<UserFilter>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },    resetState: (state) => {
      state.users = [];
      state.selectedUser = null;
      state.userDetails = null;
      state.userStats = null;
      state.loginActivity = null;
      state.error = null;
      state.pagination = initialState.pagination;
      state.filters = initialState.filters;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data.users;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.users.unshift(action.payload.data);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          const index = state.users.findIndex(user => user.id === action.payload.data.id);
          if (index !== -1) {
            state.users[index] = action.payload.data;
          }
          if (state.selectedUser?.id === action.payload.data.id) {
            state.selectedUser = action.payload.data;
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        
        // Remove user from state
        state.users = state.users.filter(user => user.id !== userId);
        
        // Clear selected user if it was deleted
        if (state.selectedUser?.id === userId) {
          state.selectedUser = null;
        }
      })      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch user details
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload.data;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle user status
      .addCase(toggleUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          const index = state.users.findIndex(user => user.id === action.payload.data.id);
          if (index !== -1) {
            state.users[index].is_active = action.payload.data.is_active;
          }          if (state.selectedUser && state.selectedUser.id === action.payload.data.id) {
            state.selectedUser.is_active = action.payload.data.is_active;
          }
        }
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Change user password
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Restore user
      .addCase(restoreUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          const index = state.users.findIndex(user => user.id === action.payload.data.id);
          if (index !== -1) {
            state.users[index] = action.payload.data;
          } else {
            // Add restored user to the list
            state.users.unshift(action.payload.data);
          }
          if (state.selectedUser?.id === action.payload.data.id) {
            state.selectedUser = action.payload.data;
          }
        }
      })
      .addCase(restoreUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch user stats
      .addCase(fetchUserStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.loading = false;
        state.userStats = action.payload.data;
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch login activity
      .addCase(fetchLoginActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })      .addCase(fetchLoginActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.loginActivity = action.payload.data;
      })
      .addCase(fetchLoginActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setSelectedUser, setFilters, clearError, resetState } = userManagementSlice.actions;
export default userManagementSlice.reducer;
